package com.neurobs.util

/** Represents an open numeric interval
 *
 * @param begin Start of the interval, generally <= end, but not checked
 * @param end end of the interval, generally >= begin, but not checked
 */
case class Interval[T](begin: T, end: T) {
	/** Absolute value of difference */
	def size(implicit num: Interval.IntervalNumeric[T]): T = num.abs(num.minus(end, begin))

	/** Whether begin and end are the same */
	def empty: Boolean = begin == end
}

object Interval {
	trait IntervalNumeric[T] {
		def minus(x: T, y: T): T
		def abs(x: T): T
		def min(x: T, y: T): T
		def max(x: T, y: T): T
		def lteq(x: T, y: T): Boolean
	}

	/** The smallest interval that contains both lhs and rhs */
	def enclosure[T](lhs: Interval[T], rhs: Interval[T])(implicit ord: IntervalNumeric[T]): Interval[T] = {
		Interval[T](ord.min(lhs.begin, rhs.begin), ord.max(lhs.end, rhs.end))
	}

	/** The intersection of lhs and rhs. If empty, the return value is empty at the minimum begin value. */
	def intersection[T](lhs: Interval[T], rhs: Interval[T])(implicit ord: IntervalNumeric[T]): Interval[T] = {
		val begin = ord.max(lhs.begin, rhs.begin)
		val end = ord.min(lhs.end, rhs.end)
		if (ord.lteq(begin, end)) Interval[T](begin, end)
		else Interval[T](begin, begin)
	}
}
